import React, { useRef } from "react";
import { Formik, Form } from "formik";
import { Col, Row } from "reactstrap";

// App
import { Autocomplete, TextField } from "../../VFX-UI";
import { SearchButton } from "../../Buttons";
import ClearButton from "../../Buttons/ClearButton";
import { SearchProps } from "../../../../types";
import { CommonBaseDto } from "../../../../app/model/api/types/dto";
import useScreenSize from "../../../hooks/useScreenSize";
import { IsSmallScreen } from "../../../../utility/helpers";
import { MAX_LARGE_SCREEN_SIZE } from "../../../../utility/constants/constants";
import { AutoCompleteRef } from "../../VFX-UI/Autocomplete/Autocomplete";

const Search: React.FC<SearchProps<CommonBaseDto>> = (props) => {
  const { searchValue, searchFields, setSearchValue, refetchGrid, dropdown, contributorTypes, agencies, methodOfGivingTypes, wrapFiltersEarly, breakActionsToNewRow, filterLayout } =
    props;
  const windowWidth = useScreenSize();
  const isSmallScreen = IsSmallScreen(windowWidth);
  const shouldWrapFilters =
    windowWidth <= MAX_LARGE_SCREEN_SIZE ||
    (wrapFiltersEarly && windowWidth <= 1280);
  const getFilterWidth = (desktopWidth: string) =>
    isSmallScreen ? "100%" : shouldWrapFilters ? "24%" : desktopWidth;
  const isCampaignDesignationsLayout = filterLayout === "campaign-designations";
  const autoCompleteRef = useRef<AutoCompleteRef>();
  const contributorTypeAutoCompleteRef = useRef<AutoCompleteRef>();
  const agencyAutoCompleteRef = useRef<AutoCompleteRef>();
  const respondedAutoCompleteRef = useRef<AutoCompleteRef>();
  const hasContributedAutoCompleteRef = useRef<AutoCompleteRef>();
  const methodOfGivingAutoCompleteRef = useRef<AutoCompleteRef>();
  const booleanOptions = [
    { label: "Yes", value: "true" },
    { label: "No", value: "false" },
  ] as any;

  return (
    <Formik
      initialValues={searchValue}
      onSubmit={async (values) => {
        let searchPayload = null;
        if (values && values.name && values.name.trim().length > 0) {
          searchPayload = {
            name: values.name,
          };
        }
        if (
          values &&
          values.personnelNumber &&
          values.personnelNumber.trim().length > 0
        ) {
          searchPayload = {
            ...searchPayload,
            personnelNumber: values.personnelNumber,
          };
        }
        if (values?.contributorTypeId) {
          searchPayload = { ...searchPayload, contributorTypeId: values.contributorTypeId };
        }
        if (values?.agencyId) {
          searchPayload = { ...searchPayload, agencyId: values.agencyId };
        }
        if (values?.responded !== null && values?.responded !== undefined) {
          searchPayload = { ...searchPayload, hasResponded: values.responded };
        }
        if (values?.hasContributed !== null && values?.hasContributed !== undefined) {
          searchPayload = { ...searchPayload, hasContributed: values.hasContributed };
        }
        if (values?.methodOfGivingId) {
          searchPayload = { ...searchPayload, methodOfGivingId: values.methodOfGivingId };
        }
        if (setSearchValue) await setSearchValue(searchPayload);
        if (refetchGrid) refetchGrid();
      }}
    >
      {({ submitForm, setFieldValue, resetForm, values }) => {
        const setDropdownValueAndSubmit = async (
          name: string,
          value: unknown,
          currentValue: unknown
        ) => {
          if (value === currentValue) return;
          await setFieldValue(name, value);
          await submitForm();
        };

        const handleChange = (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === "Enter") {
            submitForm();
          } else {
            const { name, value } = e.currentTarget;
            setFieldValue(name, value);
          }
        };
        const handleReset = async () => {
          resetForm({
            values: {
              ...searchValue,
              name: "",
              personnelNumber: "",
              contributorTypeId: null,
              agencyId: null,
              responded: null,
              hasContributed: null,
              methodOfGivingId: null,
            },
          });
          if (dropdown) {
            dropdown?.onChange([]);
            if (autoCompleteRef.current) {
              autoCompleteRef.current?.clear();
            }
          }
          if (contributorTypeAutoCompleteRef.current) {
            contributorTypeAutoCompleteRef.current.clear();
          }
          if (agencyAutoCompleteRef.current) {
            agencyAutoCompleteRef.current.clear();
          }
          if (respondedAutoCompleteRef.current) {
            respondedAutoCompleteRef.current.clear();
          }
          if (hasContributedAutoCompleteRef.current) {
            hasContributedAutoCompleteRef.current.clear();
          }
          if (methodOfGivingAutoCompleteRef.current) {
            methodOfGivingAutoCompleteRef.current.clear();
          }
          if (setSearchValue) await setSearchValue(null);
          if (refetchGrid) await refetchGrid();
        };

        return (
          <Form>
            <Row>
              <Col sm={12} className={`d-flex input-group ${shouldWrapFilters || breakActionsToNewRow ? "flex-wrap" : "flex-nowrap"}`}>
                {dropdown && (
                  <div style={{ width: getFilterWidth(isCampaignDesignationsLayout ? "10%" : "12%"), marginRight: 5, order: 1, flexShrink: 0 }}>
                    <Autocomplete
                      clearButton={dropdown?.clearButton}
                      name={dropdown?.name}
                      label={dropdown?.label}
                      options={dropdown?.options}
                      onChange={dropdown?.onChange}
                      defaultSelected={dropdown?.defaultSelected}
                      ref={autoCompleteRef}
                    />
                  </div>
                )}
                {searchFields?.name && (
                  <div
                    style={{
                      width: getFilterWidth(isCampaignDesignationsLayout ? "10%" : "11%"),
                      marginRight: 5,
                      order: 3,
                      flexShrink: 0,
                    }}
                  >
                    <TextField
                      label="Name"
                      name="name"
                      onKeyDown={handleChange}
                    ></TextField>
                  </div>
                )}
                {searchFields?.personnelNumber && (
                  <div
                    style={{
                      width: getFilterWidth(isCampaignDesignationsLayout ? "10%" : "11%"),
                      marginRight: 5,
                      order: 2,
                      flexShrink: 0,
                    }}
                  >
                    <TextField
                      label="Personnel Number"
                      name="personnelNumber"
                      onKeyDown={handleChange}
                    ></TextField>
                  </div>
                )}
                {searchFields?.contributorType && (
                  <div style={{ width: getFilterWidth(isCampaignDesignationsLayout ? "13%" : "14%"), marginRight: 5, order: 4, flexShrink: 0 }}>
                    <Autocomplete
                      clearButton
                      name="contributorTypeId"
                      label="Contributor Type"
                      options={contributorTypes ?? []}
                      defaultSelected={searchValue?.contributorTypeId ? [searchValue.contributorTypeId] : []}
                      onChange={(selectedValues: any[]) => void setDropdownValueAndSubmit(
                        "contributorTypeId",
                        selectedValues.length > 0
                          ? (selectedValues[0] as Record<string, string>)["value"]
                          : null,
                        values.contributorTypeId
                      )}
                      ref={contributorTypeAutoCompleteRef}
                    />
                  </div>
                )}
                {searchFields?.agency && (
                  <div style={{ width: getFilterWidth(isCampaignDesignationsLayout ? "13%" : "14%"), marginRight: 5, order: 7, flexShrink: 0 }}>
                    <Autocomplete
                      clearButton
                      name="agencyId"
                      label="Agency"
                      options={agencies ?? []}
                      defaultSelected={searchValue?.agencyId ? [searchValue.agencyId] : []}
                      onChange={(selectedValues: any[]) => void setDropdownValueAndSubmit(
                        "agencyId",
                        selectedValues.length > 0
                          ? (selectedValues[0] as Record<string, string>)["value"]
                          : null,
                        values.agencyId
                      )}
                      ref={agencyAutoCompleteRef}
                    />
                  </div>
                )}
                {searchFields?.responded && (
                  <div style={{ width: getFilterWidth(isCampaignDesignationsLayout ? "9%" : "10%"), marginRight: 5, order: isCampaignDesignationsLayout ? 5 : 6, flexShrink: 0 }}>
                    <Autocomplete
                      clearButton
                      name="responded"
                      label="Responded"
                      options={booleanOptions}
                      defaultSelected={
                        searchValue?.hasResponded === null || searchValue?.hasResponded === undefined
                          ? searchValue?.responded === null || searchValue?.responded === undefined
                            ? []
                            : [String(searchValue.responded)]
                          : [String(searchValue.hasResponded)]
                      }
                      onChange={(selectedValues: any[]) => void setDropdownValueAndSubmit("responded", selectedValues.length > 0 ? (selectedValues[0] as Record<string, string>)["value"] === "true" : null, values.responded)}
                      ref={respondedAutoCompleteRef}
                    />
                  </div>
                )}
                {searchFields?.hasContributed && (
                  <div style={{ width: getFilterWidth(isCampaignDesignationsLayout ? "10%" : "12%"), marginRight: 5, order: isCampaignDesignationsLayout ? 8 : 7, flexShrink: 0 }}>
                    <Autocomplete
                      clearButton
                      name="hasContributed"
                      label="Has Contributed"
                      options={booleanOptions}
                      defaultSelected={searchValue?.hasContributed === null || searchValue?.hasContributed === undefined ? [] : [String(searchValue.hasContributed)]}
                      onChange={(selectedValues: any[]) => void setDropdownValueAndSubmit("hasContributed", selectedValues.length > 0 ? (selectedValues[0] as Record<string, string>)["value"] === "true" : null, values.hasContributed)}
                      ref={hasContributedAutoCompleteRef}
                    />
                  </div>
                )}
                {searchFields?.methodOfGiving && (
                  <div style={{ width: getFilterWidth(isCampaignDesignationsLayout ? "14%" : "16%"), marginRight: 5, order: isCampaignDesignationsLayout ? 6 : 5, flexShrink: 0 }}>
                    <Autocomplete
                      clearButton
                      name="methodOfGivingId"
                      label="Method of Giving"
                      options={methodOfGivingTypes ?? []}
                      defaultSelected={searchValue?.methodOfGivingId ? [searchValue.methodOfGivingId] : []}
                      onChange={(selectedValues: any[]) => void setDropdownValueAndSubmit(
                        "methodOfGivingId",
                        selectedValues.length > 0
                          ? (selectedValues[0] as Record<string, string>)["value"]
                          : null,
                        values.methodOfGivingId
                      )}
                      ref={methodOfGivingAutoCompleteRef}
                    />
                  </div>
                )}
                <div
                  className="d-flex mb-3 "
                  style={{
                    order: 9,
                    flex: breakActionsToNewRow ? "0 0 100%" : undefined,
                  }}
                >
                  <SearchButton
                    style={{ marginRight: 5 }}
                    onClickHandler={submitForm}
                  />
                  <ClearButton
                    onClickHandler={(event) => {
                      event.preventDefault();
                      void handleReset();
                    }}
                  />
                </div>
              </Col>
            </Row>
          </Form>
        );
      }}
    </Formik>
  );
};

export default Search;
